// =============================================================================
// API Client Utility - Wrapper for AuthContext Integration
// =============================================================================

import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook that provides an authenticated API client for service classes
 * This allows services to use proper authentication without direct AuthContext dependency
 */
export const useApiClient = () => {
    const { getApiClient, isAuthenticated, user } = useAuth();
    
    return {
        apiClient: getApiClient(),
        isAuthenticated,
        user,
        // Helper method for services to check auth before making calls
        ensureAuthenticated: () => {
            if (!isAuthenticated) {
                throw new Error('User must be authenticated to perform this action');
            }
        }
    };
};

/**
 * Base API Client class that services can extend
 * Provides standardized error handling and authentication
 */
export class BaseApiService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Generic GET request with error handling
     */
    async get(endpoint, config = {}) {
        try {
            const response = await this.apiClient.get(endpoint, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * External API call with full URL (for third-party services)
     */
    async getExternal(fullUrl, config = {}) {
        try {
            const response = await this.apiClient.get(fullUrl, {
                ...config,
                baseURL: '' // Override baseURL for external calls
            });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * External POST request with full URL
     */
    async postExternal(fullUrl, data = {}, config = {}) {
        try {
            const response = await this.apiClient.post(fullUrl, data, {
                ...config,
                baseURL: '' // Override baseURL for external calls
            });
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Generic POST request with error handling
     */
    async post(endpoint, data = {}, config = {}) {
        try {
            const response = await this.apiClient.post(endpoint, data, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Generic PUT request with error handling
     */
    async put(endpoint, data = {}, config = {}) {
        try {
            const response = await this.apiClient.put(endpoint, data, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Generic DELETE request with error handling
     */
    async delete(endpoint, config = {}) {
        try {
            const response = await this.apiClient.delete(endpoint, config);
            return response.data;
        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    /**
     * Standardized error handling
     */
    handleError(error) {
        console.error('API Service Error:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
            endpoint: error.config?.url
        });

        // Handle specific error cases
        if (error.response?.status === 401) {
            console.warn('Authentication required - user will be redirected to login');
        } else if (error.response?.status === 403) {
            console.warn('Access denied - insufficient permissions');
        } else if (error.response?.status >= 500) {
            console.error('Server error - please try again later');
        }
    }
}

/**
 * Factory function to create authenticated service instances
 * Usage in components:
 * 
 * const MyComponent = () => {
 *   const { createServiceInstance } = useApiClient();
 *   const myService = createServiceInstance(MyService);
 *   // ...
 * };
 */
export const createAuthenticatedService = (ServiceClass, apiClient) => {
    return new ServiceClass(apiClient);
};